import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, fmtErr } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { fmtTRY, fmtGold, fmtDate } from "../lib/format";
import { Plus, ExternalLink } from "lucide-react";

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/events");
        setEvents(data);
      } catch (e) {
        console.error(fmtErr(e));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl">{t.dashboard}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.name} · {user?.email}</p>
        </div>
        <Button onClick={() => navigate("/events/new")} className="gap-2 rounded-full h-11 px-5" data-testid="new-event-btn">
          <Plus className="w-4 h-4" /> {t.create_event}
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground">...</div>
      ) : events.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <p className="font-serif text-2xl mb-2">İlk etkinliğinizi oluşturun</p>
          <p className="text-muted-foreground mb-6 text-sm">Sihirbaz sizi adım adım yönlendirecek.</p>
          <Button onClick={() => navigate("/events/new")} className="rounded-full" data-testid="new-event-empty-btn">{t.create_event}</Button>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {events.map((e) => (
            <Card key={e.id} className="p-6 hover:border-primary/40 transition-colors" data-testid={`event-card-${e.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-primary/80">{t.types?.[e.event_type] || e.event_type}</div>
                  <h3 className="font-serif text-2xl mt-1">{e.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(e.starts_at)}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-5 text-center">
                <div className="p-2 rounded-lg bg-muted/40">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.total_cash}</div>
                  <div className="font-mono text-sm mt-1">{fmtTRY(e.total_cash_kurus)}</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/40">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.total_gold}</div>
                  <div className="font-mono text-sm mt-1">{fmtGold(e.total_gold_mg)}</div>
                </div>
                <div className="p-2 rounded-lg bg-muted/40">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.gift_count}</div>
                  <div className="font-mono text-sm mt-1">{e.gift_count}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <Button asChild variant="outline" size="sm" className="flex-1 rounded-full" data-testid={`open-event-${e.id}`}>
                  <Link to={`/events/${e.id}`}>Yönet</Link>
                </Button>
                <Button asChild size="sm" className="flex-1 rounded-full gap-1" data-testid={`share-event-${e.id}`}>
                  <Link to={`/g/${e.invite_key}`} target="_blank">
                    <ExternalLink className="w-3 h-3" /> {t.guests_link}
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
